import type { ReviewSourceId } from '@flexbyt/shared';
import { REVIEW_SOURCE_DEFINITIONS } from '@flexbyt/shared';
import { SourceBadge, SourceGlyph, cx } from '@flexbyt/widget-preview';
import { useState } from 'react';

/**
 * The review primitives.
 *
 * ---------------------------------------------------------------------------
 * What is here, and what is emphatically not
 * ---------------------------------------------------------------------------
 * These are the *parts* of a review — a star row, a reviewer's face, the little
 * Google mark that says who vouched for it. Marketing pages need them outside a
 * widget: a rating summary in the header, an avatar row proving a source has
 * been imported, a source logo in a comparison table.
 *
 * There is deliberately **no `ReviewCard` here**. A full review card on this
 * site is drawn by `@flexbyt/widget-preview` — the same renderer as the
 * dashboard's widget editor — through `WidgetShowcase`. A second review card
 * living in the design system is precisely the marketing-only copy the brief
 * rules out, and it always wins the drift: it gets the nicer shadow, the better
 * date format, the border the real widget does not draw, and then a new
 * customer's first look at their own dashboard is a downgrade.
 *
 * The star and avatar components below are safe because they are not a widget.
 * They are the alphabet the widget is written in, and both this site and the
 * dashboard already spell with them.
 *
 * ---------------------------------------------------------------------------
 * Where they are still used, which is now less than it was
 * ---------------------------------------------------------------------------
 * `@flexbyt/widget-preview` grew its own `ReviewStars` and `ReviewerAvatar`
 * exports, and the surfaces that draw reviews *beside* a widget — the homepage
 * library, the source pages' review list — now use those directly, so the stars
 * in a list are the same gold as the stars in the widget six inches below it.
 * That was the whole reason to have one implementation, and taking it from the
 * package rather than from here is taking it one step closer to the source.
 *
 * What is left here is `RatingSummary` and `SourceTag`, which are compositions
 * this site needs and the renderer has no reason to export, plus the `Stars`
 * and `Avatar` they are built from. If a new surface needs a star row *next to
 * a widget*, take `ReviewStars` from the package; these are for the places that
 * are not about a widget at all.
 */

/**
 * A star rating.
 *
 * Gold, and gold is used for nothing else on this site — not a button, not a
 * link, not a highlight. A five-star row is gold on everybody's website; it is
 * iconography rather than interface, and the moment the accent colour borrows
 * it the stars stop being instantly readable as stars.
 *
 * Half stars are drawn with a clip rather than a second overlaid glyph, so a
 * 4.5 is one shape at any size instead of two that separate when the page
 * zooms.
 */
export function Stars({
  rating,
  size = 16,
  className,
  label,
}: {
  rating: number;
  size?: number;
  className?: string;
  /**
   * Overrides the generated accessible name.
   *
   * The default says "4.5 out of 5", which is what somebody wants to hear when
   * the stars are the whole content. When the number is also written out beside
   * them, pass `label=""` — otherwise a screen reader reads the score twice.
   */
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(5, rating));
  const accessibleName = label ?? `${clamped} out of 5`;

  return (
    <span
      className={cx('inline-flex shrink-0 gap-[2px]', className)}
      role={accessibleName ? 'img' : 'presentation'}
      aria-label={accessibleName || undefined}
      aria-hidden={accessibleName ? undefined : true}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const fill = Math.max(0, Math.min(1, clamped - star + 1));
        return (
          <svg key={star} width={size} height={size} viewBox="0 0 20 20" aria-hidden>
            <defs>
              {fill > 0 && fill < 1 ? (
                <clipPath id={`rj-star-${star}-${Math.round(fill * 100)}`}>
                  <rect x="0" y="0" width={20 * fill} height="20" />
                </clipPath>
              ) : null}
            </defs>
            <path
              d="M10 1.4l2.5 5.07 5.6.82-4.05 3.95.96 5.57L10 14.19l-5.01 2.62.96-5.57L1.9 7.29l5.6-.82z"
              className="fill-ink-200"
            />
            {fill > 0 ? (
              <path
                d="M10 1.4l2.5 5.07 5.6.82-4.05 3.95.96 5.57L10 14.19l-5.01 2.62.96-5.57L1.9 7.29l5.6-.82z"
                className="fill-gold-400"
                clipPath={
                  fill < 1
                    ? `url(#rj-star-${star}-${Math.round(fill * 100)})`
                    : undefined
                }
              />
            ) : null}
          </svg>
        );
      })}
    </span>
  );
}

/**
 * A reviewer's picture, or their initials.
 *
 * Falls back on error as well as on absence, which matters more than it sounds:
 * imported avatar URLs point at Google's and Trustpilot's CDNs, which expire
 * them and refuse requests carrying a referrer. Without `referrerPolicy` a
 * large share of real avatars 403, and a broken-image icon in the middle of a
 * page about trustworthiness is worse than initials.
 *
 * Same behaviour as the widget renderer's own avatar, for the same reasons.
 */
export function Avatar({
  src,
  name,
  size = 36,
  className,
}: {
  src?: string | null;
  name: string;
  size?: number;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);

  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
        className={cx('shrink-0 rounded-full object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cx(
        'grid shrink-0 place-items-center rounded-full bg-ink-100 font-semibold text-ink-600',
        className
      )}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
    >
      {initials}
    </span>
  );
}

/**
 * A row of overlapping reviewer faces.
 *
 * The one piece of social proof that works before any words are read. Ringed in
 * the surface colour rather than white so the stack still separates on an
 * `ink-25` section — a white ring on a grey ground is a visible halo.
 */
export function AvatarRow({
  people,
  size = 32,
  className,
  ringClass = 'ring-white',
}: {
  people: { name: string; avatar?: string | null }[];
  size?: number;
  className?: string;
  ringClass?: string;
}) {
  return (
    <span className={cx('flex items-center -space-x-2', className)}>
      {people.map((person) => (
        <Avatar
          key={person.name}
          src={person.avatar}
          name={person.name}
          size={size}
          className={cx('ring-2', ringClass)}
        />
      ))}
    </span>
  );
}

/**
 * A source logo with its name — for "imported from Google" moments.
 *
 * The mark comes from the shared package, so it is the same drawing that
 * appears in the corner of a real review card. The label comes from
 * `REVIEW_SOURCE_DEFINITIONS`, so the site cannot disagree with the product
 * about how a brand spells itself.
 */
export function SourceTag({
  source,
  className,
  showLabel = true,
}: {
  source: ReviewSourceId;
  className?: string;
  showLabel?: boolean;
}) {
  const definition = REVIEW_SOURCE_DEFINITIONS[source];

  return (
    <span
      className={cx(
        'inline-flex items-center gap-2 text-body-sm text-ink-700',
        className
      )}
    >
      <SourceGlyph source={source} className="size-4 shrink-0" />
      {showLabel ? definition.label : <span className="sr-only">{definition.label}</span>}
    </span>
  );
}

export { SourceBadge };

/**
 * An average rating with its count.
 *
 * The stars are marked `label=""` because the number is written beside them —
 * without that a screen reader announces "4.8 out of 5" and then "4.8 from 312
 * reviews", which is the same fact twice and the kind of thing that makes
 * people turn the screen reader off on a page.
 *
 * Tabular figures, so a column of these lines up on the decimal point.
 */
export function RatingSummary({
  rating,
  total,
  className,
  size = 'base',
}: {
  rating: number;
  total: number;
  className?: string;
  size?: 'base' | 'lg';
}) {
  return (
    <span className={cx('inline-flex items-center gap-2.5', className)}>
      <Stars rating={rating} size={size === 'lg' ? 20 : 16} label="" />
      <span
        className={cx(
          'nums text-ink-700',
          size === 'lg' ? 'text-body-lg' : 'text-body-sm'
        )}
      >
        <span className="font-semibold text-ink-950">{rating.toFixed(1)}</span>
        <span className="text-ink-500">
          {' '}
          from {total.toLocaleString()} review{total === 1 ? '' : 's'}
        </span>
      </span>
    </span>
  );
}
